
import React, { useState, useEffect } from 'react';
import LiveSessionParticipantsContainer from '../participants/LiveSessionParticipantsContainer';
import LiveSessionControls from '../LiveSessionControls';
import MonitoringIndicator from '../MonitoringIndicator';
import { useMicrophoneStream } from '@/hooks/useMicrophoneStream';
import { useAudioMonitor } from '@/hooks/useAudioMonitor';

interface MainContentAreaProps {
  isMicActive: boolean;
  isVideoActive: boolean;
  isAudioActive: boolean;
  isScreenSharing: boolean;
  screenStream: MediaStream | null;
  onToggleMic: () => void;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onShareScreen: () => void;
  onLeaveSession: () => void;
  isRecording: boolean;
  onToggleRecording: () => void;
  onSystemAudioStreamChange?: (stream: MediaStream | null) => void;
  systemAudioStream?: MediaStream | null;
  sessionId: string;
  userId: string;
  displayName: string;
}

const MainContentArea: React.FC<MainContentAreaProps> = ({
  isMicActive,
  isVideoActive,
  isAudioActive,
  isScreenSharing,
  screenStream,
  onToggleMic,
  onToggleVideo,
  onToggleAudio,
  onShareScreen,
  onLeaveSession,
  isRecording,
  onToggleRecording,
  onSystemAudioStreamChange,
  systemAudioStream,
  sessionId,
  userId,
  displayName
}) => {
  const [micAudioLevel, setMicAudioLevel] = useState(0);
  const { micStream, audioLevel } = useMicrophoneStream({ isMicActive });
  const { 
    isMonitoring, 
    monitorVolume, 
    startMonitoring, 
    stopMonitoring, 
    setVolume: setMonitorVolume 
  } = useAudioMonitor();

  // Update audio monitoring when streams or monitoring state changes
  useEffect(() => {
    if (isMonitoring) {
      const streams: { mic?: MediaStream | null; system?: MediaStream | null } = {};
      
      if (isMicActive && micStream) {
        streams.mic = micStream;
      }
      
      if (systemAudioStream) {
        streams.system = systemAudioStream;
      }
      
      // Only start monitoring if we have at least one stream
      if (streams.mic || streams.system) {
        startMonitoring(streams);
      }
    }
  }, [isMicActive, micStream, systemAudioStream]);

  const handleToggleMonitoring = () => {
    if (isMonitoring) {
      stopMonitoring();
    } else {
      // Will be handled by useEffect above
      const streams: { mic?: MediaStream | null; system?: MediaStream | null } = {};
      
      if (isMicActive && micStream) {
        streams.mic = micStream;
      }
      
      if (systemAudioStream) {
        streams.system = systemAudioStream;
      }
      
      if (streams.mic || streams.system) {
        startMonitoring(streams);
      }
    }
  };
  
  return (
    <div className="flex flex-col h-full w-full overflow-hidden relative">
      {/* Content area */}
      <div className="flex-1 overflow-hidden bg-gradient-to-b from-black via-[#151520] to-[#1A1F2C] pb-20 md:pb-0">
        <LiveSessionParticipantsContainer 
          isMicActive={isMicActive}
          isVideoActive={isVideoActive}
          isScreenSharing={isScreenSharing}
          screenStream={screenStream}
          sessionId={sessionId}
          userId={userId}
          displayName={displayName}
          onMicLevelChange={setMicAudioLevel}
        />
      </div>
      
      {/* Controls bar - fixed on mobile, sticky on desktop */}
      <LiveSessionControls 
        isMicActive={isMicActive}
        isVideoActive={isVideoActive}
        isAudioActive={isAudioActive}
        isScreenSharing={isScreenSharing}
        onToggleMic={onToggleMic}
        onToggleVideo={onToggleVideo}
        onToggleAudio={onToggleAudio}
        onShareScreen={onShareScreen}
        onLeaveSession={onLeaveSession}
        onLeave={onLeaveSession}
        isRecording={isRecording}
        onToggleRecording={onToggleRecording}
        onSystemAudioStreamChange={onSystemAudioStreamChange}
        micAudioLevel={audioLevel}
        isMonitoring={isMonitoring}
        onToggleMonitoring={handleToggleMonitoring}
        monitorVolume={monitorVolume}
        onMonitorVolumeChange={setMonitorVolume}
      />
      <MonitoringIndicator isMonitoring={isMonitoring} />
    </div>
  );
};

export default MainContentArea;
